import { useCallback, useSyncExternalStore } from 'react';
import { tokenStore } from './api';

/**
 * Dark mode.
 *
 * The choice lives on the `html` element as a `dark` class; every component
 * inherits it through the CSS variables in index.css. Lumina is a light
 * design: the first visit is light whatever the OS prefers, and dark mode is
 * an explicit toggle, remembered in localStorage from then on.
 *
 * The choice is honoured only while signed in. The sign-in page is always
 * light, so a dark choice applied before it showed a black loading screen
 * and then the light page; signed out, nothing is dark.
 */

const STORAGE_KEY = 'vf_theme';

type Theme = 'light' | 'dark';

function storedTheme(): Theme | null {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' ? v : null;
}

function currentTheme(): Theme {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  listeners.forEach((l) => l());
}

/**
 * Called once, before first render, so the initial paint is already in the
 * right mode. A stored session means the app is about to open; without one
 * it is about to show the (light) sign-in page.
 */
export function initTheme() {
  applyTheme(tokenStore.get() ? storedTheme() ?? 'light' : 'light');
}

/** Follow the session: the user's choice when signed in, light when not. */
export function themeForSession(signedIn: boolean) {
  applyTheme(signedIn ? storedTheme() ?? 'light' : 'light');
}

const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, currentTheme);

  const toggle = useCallback(() => {
    const next: Theme = currentTheme() === 'dark' ? 'light' : 'dark';
    localStorage.setItem(STORAGE_KEY, next);
    applyTheme(next);
  }, []);

  return { theme, toggle };
}

/** Sun/moon toggle — lives in the top bar. */
export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      onClick={toggle}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
      className="flex h-9 w-9 items-center justify-center rounded-md text-ink-secondary transition-colors duration-fast hover:bg-surface-active hover:text-ink"
    >
      {isDark ? (
        /* Sun */
        <svg aria-hidden viewBox="0 0 20 20" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <circle cx="10" cy="10" r="4" />
          <path d="M10 1.5v2M10 16.5v2M18.5 10h-2M3.5 10h-2M16 4l-1.4 1.4M5.4 14.6 4 16M16 16l-1.4-1.4M5.4 5.4 4 4" />
        </svg>
      ) : (
        /* Moon */
        <svg aria-hidden viewBox="0 0 20 20" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 11.5A7 7 0 0 1 8.5 3 7 7 0 1 0 17 11.5Z" />
        </svg>
      )}
    </button>
  );
}
